import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { AuthLayout } from "./AuthLayout";
import { PasswordStrength } from "./PasswordStrength";
import { passwordRule } from "./passwordRules";
import { useAcceptInvite, useResetPassword } from "@/api/hooks/auth";
import { errorMessage, isApiError } from "@/api/errors";
import { Field, FormError } from "@/components/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { hasSeenWelcome } from "@/lib/welcome";

type Mode = "invite" | "reset";

function makeSchema(mode: Mode) {
  return z
    .object({
      name: mode === "invite" ? z.string().trim().min(1, "auth.nameRequired") : z.string(),
      password: passwordRule,
      confirm: z.string(),
    })
    .refine((v) => v.password === v.confirm, { path: ["confirm"], message: "auth.passwordMismatch" });
}
type Values = { name: string; password: string; confirm: string };

export function SetPasswordPage({ mode }: { mode: Mode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const accept = useAcceptInvite();
  const reset = useResetPassword();
  const mutation = mode === "invite" ? accept : reset;
  const form = useForm<Values>({
    resolver: zodResolver(makeSchema(mode)),
    defaultValues: { name: "", password: "", confirm: "" },
  });
  const { errors } = form.formState;
  const password = form.watch("password");

  const onSubmit = form.handleSubmit((v) => {
    if (mode === "invite") {
      accept.mutate(
        { token, name: v.name, new_password: v.password },
        {
          onSuccess: (me) =>
            navigate(me.is_staff || hasSeenWelcome(me.id) ? "/" : "/welcome", { replace: true }),
        },
      );
    } else {
      reset.mutate(
        { token, new_password: v.password },
        { onSuccess: () => navigate("/login?reset=1", { replace: true }) },
      );
    }
  });

  const serverFieldError = isApiError(mutation.error) ? mutation.error.errors.new_password?.[0] : undefined;
  const serverError = mutation.isError && !serverFieldError ? errorMessage(mutation.error, t("errors.generic")) : null;
  const pwErrorKey = errors.password?.message;
  const pwError = pwErrorKey ? t(pwErrorKey) : (serverFieldError ?? null);

  return (
    <AuthLayout
      title={mode === "invite" ? t("auth.setPasswordTitle") : t("auth.resetPasswordTitle")}
      intro={mode === "invite" ? t("auth.setPasswordIntro") : t("auth.resetPasswordIntro")}
    >
      {!token ? (
        <FormError message={t("auth.missingToken")} />
      ) : (
        <form noValidate onSubmit={onSubmit} className="flex flex-col gap-5">
          <FormError message={serverError} />
          {mode === "invite" ? (
            <Field label={t("auth.name")} error={errors.name?.message ? t(errors.name.message) : null}>
              {(p) => <Input {...p} autoComplete="name" {...form.register("name")} />}
            </Field>
          ) : null}
          <Field label={t("auth.newPassword")} hint={t("auth.passwordHint")} error={pwError}>
            {(p) => <Input {...p} type="password" autoComplete="new-password" {...form.register("password")} />}
          </Field>
          <PasswordStrength value={password} />
          <Field label={t("auth.confirmPassword")} error={errors.confirm?.message ? t(errors.confirm.message) : null}>
            {(p) => <Input {...p} type="password" autoComplete="new-password" {...form.register("confirm")} />}
          </Field>
          <Button type="submit" className="w-full" disabled={mutation.isPending}>
            {mutation.isPending ? t("auth.saving") : t("auth.setPassword")}
          </Button>
        </form>
      )}
      <p className="mt-6 text-sm">
        <Link to="/login" className="underline">
          {t("auth.backToSignIn")}
        </Link>
      </p>
    </AuthLayout>
  );
}
